import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { HELP_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { InternalNoticeConfig } from "../types";

const CONFIG_QUERY_KEY = ["internalNotices", "config"] as const;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Splits the holiday textarea (one date per line or comma separated) into ISO dates. */
export const parseHolidayDates = (text: string): string[] | null => {
  const dates = text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const valid = dates.every(
    (date) => ISO_DATE.test(date) && !Number.isNaN(Date.parse(date)),
  );
  return valid ? Array.from(new Set(dates)).sort() : null;
};

const ConfigForm = ({ config }: { config: InternalNoticeConfig }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [windowMinutes, setWindowMinutes] = useState(
    String(config.groupingWindowMinutes),
  );
  const [weekends, setWeekends] = useState(config.summariesOnWeekends);
  const [holidays, setHolidays] = useState(config.holidayDates.join("\n"));

  useEffect(() => {
    setWindowMinutes(String(config.groupingWindowMinutes));
    setWeekends(config.summariesOnWeekends);
    setHolidays(config.holidayDates.join("\n"));
  }, [config]);

  const save = useMutation({
    mutationFn: (next: InternalNoticeConfig) =>
      dataProvider.saveInternalNoticeConfig(next),
    onSuccess: () => {
      notify("crm.internalNotices.config.saved");
      return queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY });
    },
    onError: () =>
      notify("crm.internalNotices.config.save_error", { type: "error" }),
  });

  const minutes = Number(windowMinutes);
  const holidayDates = parseHolidayDates(holidays);
  const isValid = Number.isInteger(minutes) && minutes > 0 && !!holidayDates;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!isValid || !holidayDates) return;
        save.mutate({
          groupingWindowMinutes: minutes,
          summariesOnWeekends: weekends,
          holidayDates,
        });
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="internal-notice-window">
          {translate("crm.internalNotices.config.window")}
        </Label>
        <Input
          id="internal-notice-window"
          type="number"
          min={1}
          inputMode="numeric"
          className="h-11 sm:h-9"
          value={windowMinutes}
          onChange={(event) => setWindowMinutes(event.target.value)}
        />
        <p className={HELP_CLASS}>
          {translate("crm.internalNotices.config.window_help")}
        </p>
      </div>
      <div className="flex min-h-11 items-center gap-3">
        <Switch
          id="internal-notice-weekends"
          checked={weekends}
          onCheckedChange={setWeekends}
        />
        <Label htmlFor="internal-notice-weekends" className="text-[15px]">
          {translate("crm.internalNotices.config.weekends")}
        </Label>
      </div>
      <div className="space-y-1">
        <Label htmlFor="internal-notice-holidays">
          {translate("crm.internalNotices.config.holidays")}
        </Label>
        <Textarea
          id="internal-notice-holidays"
          rows={3}
          value={holidays}
          aria-invalid={!holidayDates}
          onChange={(event) => setHolidays(event.target.value)}
        />
        <p className={HELP_CLASS}>
          {translate("crm.internalNotices.config.holidays_help")}
        </p>
      </div>
      {!isValid ? (
        <p role="alert" className="text-[15px] text-destructive">
          {translate("crm.internalNotices.config.invalid")}
        </p>
      ) : null}
      <Button
        type="submit"
        className="min-h-11"
        disabled={!isValid || save.isPending}
      >
        {translate("ra.action.save")}
      </Button>
    </form>
  );
};

/**
 * Almikia Epic EN (TASK-091): the data-valued config of the module (grouping window, weekend
 * summaries flag, holiday list), stored in `internal_notice_config` and editable by direccion only
 * (RLS). The summary times, template names and the free quota stay server-side data, not edited
 * here.
 */
export const InternalNoticeConfigCard = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const config = useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: () => dataProvider.getInternalNoticeConfig(),
    retry: false,
  });

  return (
    <Card data-testid="internal-notice-config-card">
      <CardContent className="space-y-3 p-4">
        <h2 className="text-lg font-semibold">
          {translate("crm.internalNotices.config.title")}
        </h2>
        {config.error ? (
          <ListErrorState
            message={translate("crm.internalNotices.config.load_error")}
            onRetry={() => config.refetch()}
          />
        ) : config.data === undefined ? (
          <ListCardsSkeleton rows={1} />
        ) : (
          <ConfigForm config={config.data} />
        )}
      </CardContent>
    </Card>
  );
};
